import { useMemo } from 'react'

import { matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { RoleModel, RoleRequestOptions } from '@domain-group/realhub-backend-api-types'

const watchEntityKeys = ['roles']

type RoleFilters = {
  string?: string,
}

type UseRolesOptions = {
  filters?: RoleFilters,
  performHttpRequests?: boolean,
  requestOptions?: RoleRequestOptions,
}

function useRoles(options: UseRolesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    string: filterString,
  } = filters

  const {
    updatedEntities: { roles: rolesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { roles } = useSelector(reduxState => reduxState.entities)

  const filteredRoles = useMemo(() => {
    const filtered = Object.values(roles).filter((role: RoleModel) => {
      const { id, title } = role

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const stringMatch = matchId || matchTitle

      return stringMatch
    })

    // @ts-expect-error TS2345
    return sortArrayBy(filtered, 'asc', 'name')
  }, [rolesUpdatedAt, JSON.stringify(filters)])

  const filteredRolesCount = filteredRoles.length
  const hasFilteredRoles = !!filteredRolesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredRolesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingRoles } = useReduxAction(
    'roles',
    'loadRoles',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredRoles,
    filteredRolesCount,
    hasFilteredRoles,
    loading: loadingRoles,
  }
}

export default useRoles
